<template>
  <div class="p-page-create-edit" v-if="!loading">
    <page-title
      marginBottom="0"
      subTitle="Create single or multiple banner sets"
    >
      <span v-if="editMode">Edit Banner Set</span>
      <span v-else>Create Banner Set</span>
    </page-title>

    <section-block :headerOverflowHidden="true">
      <template slot="header">
        <form-field
          dynamic-width
          ghost
          large
          min-width="35ch"
          is-inline
          v-model="currentBannerSet.name"
          placeholder="Give your banner set a name here"
        ></form-field>
      </template>

      <template slot="actions">
        <dropdown class="u-show-tablet-down">
          <dropdown-action @click="onClickViewPage"
            >Preview Banner</dropdown-action
          >
          <dropdown-action primary="true" v-if="!editMode" type="button"
            >Save</dropdown-action
          >
          <dropdown-action
            primary="true"
            v-if="editMode"
            type="button"
            :disabled="duplicateResource == true"
            @click="saveSlider"
            >Update</dropdown-action
          >
          <dropdown-action
            @click="handleActivate"
            v-if="editMode && currentBannerSet.status == 'inactive'"
            type="button"
            >Activate</dropdown-action
          >
        </dropdown>
        <div class="u-hide-tablet">
          <base-button
            @click="handleActivate"
            v-if="editMode && currentBannerSet.status == 'inactive'"
            type="button"
            >Activate</base-button
          >
          <base-button
            @click="handleActivate"
            v-if="editMode && currentBannerSet.status != 'inactive'"
            type="button"
            >Deactivate</base-button
          >
          <base-button type="button" @click="onPreview">Preview</base-button>
          <base-button
            primary="true"
            v-if="!editMode"
            type="button"
            @click="saveSlider"
            >Save</base-button
          >
          <base-button
            primary="true"
            v-if="editMode"
            type="button"
            :disabled="duplicateResource == true"
            @click="saveSlider"
            >Update</base-button
          >
          <base-button
            @click="handleActivate"
            v-if="editMode && currentBanner.status == 'inactive'"
            type="button"
            >Activate</base-button
          >
        </div>
      </template>

      <loading v-if="bannerLoading" />

      <section class="row" v-else>
        <div class="col-xs-12 col-md-8">
          <form-field
            label="Banner Image"
            name="file_id"
            helper-text="Select an image to display"
          >
            <form-files
              @change="onImageSelect"
              :fileTypes="['png', 'jpg', 'gif']"
              ref="imageUpload"
            />

            <p>Or upload an image here</p>
            <file-upload
              name="bannerImg"
              @uploaded="onFileUploaded"
              resource="content1"
              bypass-duplicate
              :fileTypes="['png', 'jpg', 'gif']"
              :multiple="false"
            ></file-upload>

            <p v-if="hasBannerImage">
              <strong>Current banner:</strong><br />
              <image-loader :image-id="bannerImageId" class="max-w-200" />
            </p>
          </form-field>
          <form-field
            label="Banner Title"
            helper-text="The title to display on the banner"
            required
            v-model="currentBanner.title"
            data-vv-as="title"
            id="title"
            name="title"
            v-validate="{ required: true }"
            full-width
            :error="errors.first('title')"
          />
          <form-field
            label="Banner Duration"
            helper-text="How long in seconds do you want the banner to display? (Only works with multiple banners)"
            :required="
              currentBannerSet.banners && currentBannerSet.banners.length > 1
            "
            v-model="currentBanner.duration"
            data-vv-as="duration"
            id="duration"
            name="duration"
            v-validate="{
              required:
                currentBannerSet.banners && currentBannerSet.banners.length > 1,
            }"
            full-width
            :error="errors.first('duration')"
          />

          <form-field
            label="Button Text"
            helper-text="Text to display on the link button"
            v-model="currentBanner.button_text"
            full-width
          />

          <section class="resource-src">
            <tabs>
              <tabs-item
                :key="0"
                :active="showResource"
                @click="showResource = true"
                >Link to resource</tabs-item
              >
              <tabs-item
                :key="1"
                :active="!showResource"
                @click="showResource = false"
                >External link</tabs-item
              >
            </tabs>

            <div v-if="showResource">
              <div
                v-if="currentBanner.resource_id || currentBanner.resource_title"
              >
                <p><strong>Current Resource:</strong></p>
                <p class="file-highlight">
                  {{ currentBanner.resource_title }}
                  <base-button type="button" @click="removeResource"
                    >Remove</base-button
                  >
                </p>
              </div>
              <form-field
                label="Link to resource"
                name="document_file"
                helper-text="Link to a resource"
                v-else
              >
                <form-multiselect
                  :options="resource_types"
                  v-model="resourceType"
                  :show-valid-tick="false"
                  show-drop-icon
                  ref="filter"
                  autocomplete="off"
                  placeholder="Select resource type"
                  :multiple="false"
                  label="name"
                  track-by="name"
                  @input="setResourceType"
                  :limit="1"
                  long
                  data-vv-as="resource type"
                  name="resource_type"
                  id="resource_type"
                  :error="errors.first('resource_type')"
                />
              </form-field>

              <form-field
                label="Search for a resource"
                helper-text="Type to search for a resource"
                required
                v-if="searchIndex && !currentBanner.resource_id"
                full-width
                v-model="resourceSearch"
                @input="searchContent"
                data-vv-as="resource"
                name="resource"
                id="resource"
                :error="errors.first('resource')"
              />

              <p
                v-if="
                  searchResults && searchResults.length < 1 && resourceSearch
                "
              >
                Sorry, there are no results. Please change your search query.
              </p>

              <div v-if="searchResults && searchResults.length > 0">
                <p><strong>Results:</strong></p>

                <ol class="suggestions listless">
                  <li v-for="result in searchResults" :key="result.id">
                    <button
                      @click="setResource(result)"
                      type="button"
                      class="file-highlight is-link"
                    >
                      {{ result[queryField] }}
                    </button>
                  </li>
                </ol>
              </div>
            </div>

            <form-field
              full-width
              label="External Link"
              name="link_url"
              helper-text="If you want to link to an external page"
              v-model="currentBanner.link_url"
              v-validate="{ required: true, min: 5 }"
              data-vv-as="title"
              :error="errors.first('link_url')"
              v-else
            ></form-field>

            <message
              type="warning"
              v-if="currentBanner.link_url && currentBanner.resource_id"
            >
              <p>
                You currently have a resource and external link set up for this
                banner. Please remove one of these values before saving the
                banner.
              </p>
            </message>
          </section>
          <form-field
            label="Link to video?"
            helper-text="You can choose to link the banner to a video.  The video will play in a modal overlay"
            full-width
          >
            <section class="video-src">
              <tabs>
                <tabs-item
                  :key="0"
                  :active="showVideoResource"
                  @click="showVideoResource = true"
                  >Link to internal video</tabs-item
                >
                <tabs-item
                  :key="1"
                  :active="!showVideoResource"
                  @click="showVideoResource = false"
                  >External link</tabs-item
                >
              </tabs>

              <div v-if="showVideoResource">
                <div v-if="currentBanner.video_id">
                  <p><strong>Current Video:</strong></p>
                  <p class="file-highlight">
                    {{ currentBanner.video_id.name }}
                    <base-button type="button" @click="removeVideo"
                      >Remove</base-button
                    >
                  </p>
                </div>
                <form-field
                  label="Link to internal video"
                  name="document_file"
                  helper-text="Link to a video already uploaded or upload a new video"
                  v-else
                >
                  <form-files
                    @change="onVideoSelect"
                    :fileTypes="['mov', 'mp4', 'avi', 'wmv', 'flv', 'm4v']"
                    ref="videoUpload"
                  />

                  <p>Or upload a video here</p>
                  <video-upload
                    name="bannerVideo"
                    @uploaded="onVideoUploaded"
                    resource="content2"
                    bypass-duplicate
                    :fileTypes="['mov', 'mp4', 'avi', 'wmv', 'flv', 'm4v']"
                    :multiple="false"
                  />
                </form-field>
              </div>

              <form-field
                full-width
                label="External Link"
                name="link_url"
                helper-text="If you want to link to an external video"
                v-model="currentBanner.video_url"
                v-validate="{ required: true, min: 5 }"
                data-vv-as="title"
                :error="errors.first('link_url')"
                v-else
              ></form-field>

              <message
                type="warning"
                v-if="currentBanner.video_url && currentBanner.video_id"
              >
                <p>
                  You currently have a video and external video link set up for
                  this banner. Please remove one of these values before saving
                  the banner.
                </p>
              </message>
            </section>
          </form-field>
          <base-button
            v-if="
              this.currentBannerSet.banners &&
                currentBannerSet.banners.length > 1
            "
            danger
            @click="deleteBanner(currentIndex)"
          >
            Delete Banner
          </base-button>
        </div>
        <div class="col-xs-12 col-md-4">
          <sortable-list v-model="currentBannerSet.banners" enabled>
            <sortable-item
              v-for="(banner, index) in currentBannerSet.banners"
              :key="index"
              @moveUp="moveItemUp(index)"
              @moveDown="moveItemDown(index)"
              enabled
              @click="setActiveBanner(index)"
              >{{
                banner.title ? banner.title : `Banner ${index + 1}`
              }}</sortable-item
            >
          </sortable-list>
          <p
            v-if="
              this.currentBannerSet.banners &&
                currentBannerSet.banners.length > 1
            "
          >
            Click on a banner name above to switch between banners.
          </p>
          <p>
            <base-button @click="createBanner">
              Add a banner
            </base-button>
          </p>
        </div>
      </section>
    </section-block>
    <div class="modal-preview" v-if="showPreview">
      <hero-slider-multi :slider="preview" display />
      <p>
        <base-button @click="showPreview = false">Close Preview</base-button>
      </p>
    </div>
  </div>

  <loading v-else></loading>
</template>
<script>
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import FileUpload from '@/components/FileUpload';
import VideoUpload from '@/components/VideoUpload';
import Pagination from '@/components/pagination';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination';
import ImageLoader from '@/components/elements/ImageLoader';
import SortableList from '@/components/SortableList';
import SortableItem from '@/components/SortableItem';
import FormFiles from '@/components/FormFiles';
import BaseButton from '../../../../components/BaseButton.vue';

const HeroSliderMulti = () => import('@/components/HeroSliderMulti');
const Dropdown = () => import('@/components/Dropdown');
const DropdownAction = () => import('@/components/DropdownAction');

export default {
  name: 'BannerAdminAddEdit',

  components: {
    Pagination,
    VuetablePagination,
    FileUpload,
    VideoUpload,
    ImageLoader,
    Dropdown,
    DropdownAction,
    SortableList,
    SortableItem,
    FormFiles,
    HeroSliderMulti,
    BaseButton,
  },

  methods: {
    async onPreview() {
      // here we need to take everything in state, put in preview object and open page
      await this.$store.dispatch('Banner/savePreview', this.currentBannerSet);
      this.showPreview = true;
    },
    async saveSlider() {
      // TODO: Validate
      const slider = { ...this.currentBannerSet };
      const action = this.editMode ? 'updateSlider' : 'saveSlider';
      slider.banners.forEach((ban) => {
        ban.duration = ban.duration ? ban.duration * 1000 : null;
        // can only be resource or link
        // assume that if there is a link that is the priority
        if (ban.link_url) {
          ban.resource = null;
          ban.resource_id = null;
        } else if (this.resourceType) {
          // quick fix to allow for non link and non resource banners TODO: needs logic review
          ban.link_url = null;
          // for the resource, we need the type
          ban.resource = this.resourceType.id;
        }
        if (ban.video_id && ban.video_id.id) {
          ban.video_id = ban.video_id.id;
        }
      });
      const bannerConfirm = await this.$store.dispatch(
        `Banner/${action}`,
        slider,
      );
      if (!this.editMode) {
        this.$router.push({
          name: 'AdminBannerEdit',
          params: { id: bannerConfirm.data.data.slider[0].id },
        });
      } else {
        this.loadBanner(this.$route.params.id);
      }
    },
    onVideoSelect(val) {
      const data = JSON.parse(val);
      // this.currentBanner.video_id = data;
      this.currentBannerSet.banners[this.currentIndex].video_id = data;
    },
    onImageSelect(val) {
      const data = JSON.parse(val);
      this.currentBannerSet.banners[this.currentIndex].file_id = data.id;
    },
    setResourceType(val) {
      this.searchResults = null;
      if (val) {
        this.resourceType = val;
      }
    },
    getResourceTitle() {
      if (this.currentBanner.resource_title) {
        this.resourceTitle = this.currentBanner.resource_title;
        return;
      }
      const banner = this.banners.filter(banner => banner.id == this.$route.params.id);
      if (banner && banner.length > 0) {
        this.resourceTitle = banner[0].resource.title
          ? banner[0].resource.title
          : banner[0].resource.name;
      }
    },
    async handleActivate() {
      const status =
        this.currentBannerSet.status === 'active' ? 'inactive' : 'active';
      await this.$store.dispatch('Banner/setStatusSlider', {
        status,
        id: this.currentBannerSet.id,
      });
      // clone
      this.currentBannerSet.status = status !== 'active' ? 'inactive' : 'active';
    },
    onFileUploaded(file) {
      // this.currentBanner.file_id = file.id;
      this.currentBannerSet.banners[this.currentIndex].file_id = file.id;
    },
    onVideoUploaded(file) {
      // this.currentBanner.video_id = file;
      this.currentBannerSet.banners[this.currentIndex].video_id = file;
    },
    removeResource() {
      this.currentBanner.resource_id = null;
      this.currentBanner.resource = null;
      this.currentBanner.resource_title = null;
      this.resourceTitle = null;
      this.resourceSearch = null;
    },
    removeVideo() {
      this.currentBanner.video_id = null;
      this.videoSearch = null;
    },
    searchContent: debounce(function () {
      this.searchResults = [];
      const index = this.searchIndex;
      const term = this.resourceSearch;

      this.$store
        .dispatch('Banner/resourceSearch', { index, term })
        .then((res) => {
          let type;

          this.searchResults = res.response;
          this.queryField = res.queryField;
        })
        .catch((err) => {
          console.log('TCL: err', { ...err });
        });
    }, 2000),

    setResource(item) {
      this.currentBanner.resource_id = item.id;
      this.currentBanner.resource_title = item[this.queryField];
      this.resourceTitle = item[this.queryField];
      this.searchResults = null;
    },
    moveItemUp(from) {
      const newArray = [...this.currentBannerSet.banners];
      if (from > 0) {
        newArray.splice(from, 0, newArray.splice(from - 1, 1)[0]);
        this.currentBannerSet.banners = newArray;
      }
    },

    // move an item down the list
    moveItemDown(from) {
      const newArray = [...this.currentBannerSet.banners];

      if (from < newArray.length - 1) {
        const to = from + 1;
        newArray.splice(from, 0, newArray.splice(to, 1)[0]);
        this.currentBannerSet.banners = newArray;
      }
    },
    createBanner() {
      this.bannerLoading = true;
      const template = { ...this.$store.state.Banner.emptyBanner };
      Object.keys(template).forEach((key) => {
        template[key] = null;
      });
      this.currentBannerSet.banners.push(template);
      this.currentIndex = this.currentBannerSet.banners.length - 1;
      // reset validation
      this.$nextTick(() => {
        setTimeout(() => {
          this.bannerLoading = false;
        }, 500);
        // this.$validator.reset();
        // this.$refs["imageUpload"].reset();
        // this.$refs["videoUpload"].reset();
        // this.resourceType = null;
        // this.resourceSearch = null;
      });
    },
    deleteBanner(index) {
      this.$store.dispatch('createDialog', {
        // TODO: This does the right thing, but do we need to create its own
        // method in the store so we can alter the confirmation etc.?
        confirm: () => {
          this.currentBannerSet.banners = this.currentBannerSet.banners.filter((item, ind) => ind !== index);
          this.currentBannerSet.banners.every((el, ind) => {
            this.setActiveBanner(ind);
            return false;
          });
          console.log('New banner set after delete', this.currentBannerSet.banners);
        },
      });
    },
    setActiveBanner(index) {
      this.currentIndex = index;
      this.errors.clear();
      this.bannerLoading = true;
      this.$nextTick(() => {
        setTimeout(() => {
          this.bannerLoading = false;
        }, 500);
        // this.$validator.reset();
        // this.$refs["imageUpload"].reset();
        // this.$refs["videoUpload"].reset();
        // this.resourceType = null;
        // this.resourceSearch = null;
      });
    },
    async loadBanner(id) {
      try {
        const resp = await this.$store.dispatch('Banner/loadSlider', {
          bannerId: id,
          saveInState: true,
        });
        console.log(
          '🚀 ~ file: AddEditPage.vue ~ line 543 ~ loadBanner ~ resp',
          resp,
        );
        this.showResource = !this.currentBanner.link_url;
        this.bannerType =
          this.currentBannerSet.banners &&
          this.currentBannerSet.banners.length > 1
            ? 'multiple'
            : 'single';

        // need to put back the durations
        if (this.currentBannerSet.banners) {
          this.currentBannerSet.banners.forEach((ban) => {
            if (ban.duration && ban.duration > 0) {
              ban.duration /= 1000;
            }
          });
        }
        this.loading = false;
        this.editMode = true;
      } catch (err) {
        console.log(
          '🚀 ~ file: AddEditPage.vue ~ line 546 ~ loadBanner ~ err',
          err,
        );
        this.loading = false;
        this.showError = true;
      }
      // if (this.currentBanner.link_url) {
      //   this.showResource = false;
      // }
    },
  },
  data() {
    return {
      editMode: false,
      loading: true,
      bannerLoading: false,
      bannerType: null,
      currentIndex: 0,
      showError: false,
      showResource: true,
      showVideoResource: true,
      resourceType: null,
      resourceSearch: null,
      searchResults: null,
      queryField: null,
      resourceTitle: null,
      showPreview: false,
    };
  },

  computed: {
    ...mapState('Banner', ['resource_types']),
    ...mapState({
      currentBannerSet: state => state.Banner.currentBannerSetEdit,
    }),
    preview() {
      const preview = this.$store.state.Banner.preview;
      preview.banners.forEach((banner) => {
        banner.duration *= 1000;
      });
      return preview;
    },
    hasBannerImage() {
      if (
        !this.currentBannerSet.banners ||
        this.currentBannerSet.banners.length === 0
      ) {
        return false;
      }
      return (
        this.currentBannerSet.banners[this.currentIndex].file_id ||
        (this.currentBannerSet.banners[this.currentIndex].file &&
          this.currentBannerSet.banners[this.currentIndex].file.id)
      );
    },
    bannerImageId() {
      if (this.currentBanner.file_id) {
        return this.currentBanner.file_id;
      }
      if (this.currentBanner.file && this.currentBanner.file.id) {
        return this.currentBanner.file.id;
      }
      return null;
    },
    searchIndex() {
      if (this.resourceType) {
        const explode = this.resourceType.id.split('_');
        return `/${explode[0]}/${explode[1]}s/flat`;
      }
      return '';
    },
    duplicateResource() {
      return (
        this.currentBanner.link_url &&
        (this.currentBanner.link_url !== '' &&
          this.currentBanner.link_url !== null) &&
        (this.currentBanner.resource_id &&
          (this.currentBanner.resource_id !== '' &&
            this.currentBanner.resource_id !== null))
      );
    },
    currentBanner() {
      if (
        !this.currentBannerSet.banners ||
        !this.currentBannerSet.banners[this.currentIndex]
      ) {
        return {
          id: null,
          title: null,
          description: null,
          button_text: null,
          link_url: null,
          resource: null,
          resource_id: null,
          resource_title: null,
          file_id: null,
          duration: null,
          video_id: null,
          video_url: null,
        };
      }
      return this.currentBannerSet.banners[this.currentIndex] || null;
    },
  },
  async mounted() {
    if (!this.$route.params.id) {
      await this.$store.dispatch('Banner/getBaseSlider');
      this.loading = false;
    } else {
      await this.loadBanner(this.$route.params.id);
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.c-banner {
  &__type {
    text-align: center;

    button {
      background: transparent;
      border: 3px solid $colour-nhs-core-blue;
      border-radius: 8px;
      cursor: pointer;
      padding: 10px;

      &:hover {
        background: $colour-nhs-pale-grey;
      }
    }

    ul {
      align-items: center;
      display: flex;
      gap: 2rem;
      justify-content: center;
      margin: 0;
      padding: 0;

      li {
        list-style-type: none;
        margin: 0;
        padding: 0;
      }
    }
  }
}
.suggestions {
  margin-bottom: 10px;
  border-radius: 4px 4px 0 0;
  overflow: hidden;
  li {
    display: block;
  }
}
.file-highlight {
  padding: 10px;
  background: white;
  border: 2px solid #c9ced4;
  display: flex;
  border-radius: 4px 4px 0 0;
  width: 100%;
  text-align: left;
  align-items: center;
  justify-content: space-between;

  &.is-link {
    cursor: pointer;

    &:hover {
      background: $colour-nhs-core-blue;
      color: white;
      border-color: $colour-nhs-core-blue;
    }
  }
}

li + li .file-highlight {
  border-radius: 0;
  border-top-width: 0;
}
.suggestions li:last-of-type .file-highlight {
}
.suggestions li:last-of-type .file-highlight {
  border-radius: 0 0 4px 4px;
}

.max-w-200 {
  max-width: 200px;
}

.modal-preview {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0 10vw;
  box-sizing: border-box;
  z-index: 99;

  &:before {
    content: "";
    background: rgba(0, 0, 0, 0.5);
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
  }

  ::v-deep(button) {
    position: relative;
  }
}
</style>
